import React, { useState } from "react";
import { EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Form,
  Input,
  Button,
  message,
  Popconfirm,
  Switch,
  Image as AntdImage,
} from "antd";
import ButtonWrapper from "../../../components/reuseComp/ButtonWrapper";
import UploadFoodImage from "../../../../lib/UploadFoodImage";
import { getSingleImageUrl } from "../../../../lib/utils";
import { IAuthorPayload } from "../../../../types/api/author";
import {
  useAuthorDelete,
  useAuthorList,
  useAuthorSave,
} from "../../../../hooks/api/author/hook";

interface Props {
  refetch?: () => void;
}

const AuthorListingSection: React.FC<Props> = ({
  refetch: externalRefetch,
}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const [editingAuthor, setEditingAuthor] = useState<IAuthorPayload | null>(
    null,
  );

  const { data, refetchWithQuery: internalRefetch } = useAuthorList({
    page: 1,
    limit: 20,
  });
  const authorList: IAuthorPayload[] = data?.data || [];
  const qData = data?.qData ?? { page: 1, limit: 20 };

  const { mutate: saveAuthor, isPending: isSaving } = useAuthorSave({ qData });
  const { mutate: deleteAuthor } = useAuthorDelete({ qData });

  const triggerRefetch = () => {
    externalRefetch?.() || internalRefetch(qData);
  };

  const handleAdd = () => {
    setEditingAuthor(null);
    form.resetFields();
    setIsEditing(true);
  };

  const handleEdit = (author: IAuthorPayload) => {
    setEditingAuthor(author);
    form.setFieldsValue({
      name: author.name,
      status: author.status,
      avatar: author.avatar || "",
      designation: author.designation,
    });
    setIsEditing(true);
  };

  const handleCancel = () => {
    setEditingAuthor(null);
    form.resetFields();
    setIsEditing(false);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const payload: IAuthorPayload = {
        _id: editingAuthor?._id || "",
        name: values.name,
        avatar: getSingleImageUrl(values.avatar) || editingAuthor?.avatar || "",
        designation: values.designation,
        status: values.status ?? true,
      };

      saveAuthor(payload, {
        onSuccess: () => {
          messageApi.success(editingAuthor ? "Author updated" : "Author added");
          handleCancel();
          triggerRefetch();
        },
        onError: () => messageApi.error("Failed to save author"),
      });
    } catch (err) {
      console.log("Validation failed:", err);
    }
  };

  const handleDelete = (id: string) => {
    deleteAuthor(
      { ids: id },
      {
        onSuccess: () => {
          messageApi.success("Author deleted");
          triggerRefetch();
        },
        onError: () => messageApi.error("Delete failed"),
      },
    );
  };

  return (
    <>
      {contextHolder}

      {!isEditing ? (
        <>
          <div className="flex justify-end mb-4">
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              Add Author
            </Button>
          </div>
          <div className="rounded-[20px] overflow-hidden hidden sm:block">
            <table className="min-w-full">
              <thead>
                <tr className="bg-primary text-white">
                  <th className="py-3 px-4 text-left">Avatar</th>
                  <th className="py-3 px-4 text-left">Name</th>
                  <th className="py-3 px-4 text-left">Designation</th>
                  <th className="py-3 px-4 text-left">Status</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {authorList.map((author) => (
                  <tr key={author._id} className="bg-[#F9F9F9] border-b-2">
                    <td className="py-3 px-4">
                      {author.avatar ? (
                        <AntdImage
                          src={author.avatar}
                          alt={author.name}
                          width={50}
                          height={50}
                          className="rounded-full object-cover"
                          preview={false}
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gray-200 rounded-full border-2 border-dashed" />
                      )}
                    </td>
                    <td className="py-3 px-4 font-medium">{author.name}</td>
                    <td className="py-3 px-4 font-medium">
                      {author.designation}
                    </td>
                    <td className="py-3 px-4">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          author.status
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {author.status ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <EditOutlined
                        onClick={() => handleEdit(author)}
                        className="cursor-pointer text-primary text-xl mr-4"
                      />
                      <Popconfirm
                        title="Delete this author?"
                        onConfirm={() => handleDelete(author._id)}
                      >
                        <DeleteOutlined className="cursor-pointer text-red-600 text-xl" />
                      </Popconfirm>
                    </td>
                  </tr>
                ))}
                {authorList.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center py-10 text-gray-500">
                      No authors found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="block sm:hidden space-y-3">
            {authorList.map((author) => (
              <div key={author._id} className="bg-[#F9F9F9] p-4 rounded-lg">
                <div className="flex items-center gap-4">
                  {author.avatar ? (
                    <AntdImage
                      src={author.avatar}
                      alt={author.name}
                      width={60}
                      height={60}
                      className="rounded-full object-cover"
                      preview={false}
                    />
                  ) : (
                    <div className="w-16 h-16 bg-gray-200 rounded-full border-2 border-dashed" />
                  )}
                  <div className="flex-1">
                    <h4 className="font-semibold">{author.name}</h4>
                    <h4 className="font-medium">{author.designation}</h4>
                    <p className="text-sm text-gray-600">
                      {author.status ? "Active" : "Inactive"}
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <EditOutlined
                      onClick={() => handleEdit(author)}
                      className="text-primary text-xl"
                    />
                    <Popconfirm
                      title="Delete?"
                      onConfirm={() => handleDelete(author._id)}
                    >
                      <DeleteOutlined className="text-red-600 text-xl" />
                    </Popconfirm>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className="bg-[#F9F9F9] p-6 rounded-xl">
          <Form form={form} layout="vertical">
            <Form.Item
              label="Author Name"
              name="name"
              rules={[{ required: true }]}
            >
              <Input placeholder="Enter author name" />
            </Form.Item>

            <Form.Item
              label="Author Designation"
              name="designation"
              rules={[{ required: true }]}
            >
              <Input placeholder="Enter author designation" />
            </Form.Item>

            <Form.Item label="Status" name="status" valuePropName="checked">
              <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
            </Form.Item>

            <Form.Item
              label="Avatar"
              name="avatar"
              rules={[{ required: !editingAuthor, message: "Avatar required" }]}
            >
              <UploadFoodImage maxUploadCount={1} />
            </Form.Item>
          </Form>

          <div className="flex justify-end gap-3 mt-6">
            <ButtonWrapper onClick={handleCancel}>Cancel</ButtonWrapper>
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={isSaving}
            >
              {editingAuthor ? "Update" : "Save"} Author
            </ButtonWrapper>
          </div>
        </div>
      )}
    </>
  );
};

export default AuthorListingSection;
